import { HttpError, useCan, useCreate, useGo, useList, useTranslation, useUpdate, Link, CanAccess } from "@refinedev/core";
import InfoCard from "@components/molecules/InfoCard";
import CircularButtonWithToggle, { State } from "@components/organisms/CircularButtonWithToggle";
import { FaLifeRing, FaCalendarDays, FaCalendarPlus } from "react-icons/fa6";
import { MdAssistWalker } from "react-icons/md";
import { IAvailability } from "interfaces";
import dayjs from "dayjs";
import { useAppCalendar } from "hooks/calendar/useAppCalendar";
import { useMemo } from "react";
import relativeTime from "dayjs/plugin/relativeTime";
import calendar from "dayjs/plugin/calendar";
import { useAtomValue } from "jotai";
import { blogIdAtom } from "@pages/content/state";
import { blogger_v3, calendar_v3 } from "googleapis"
import { logger } from "@utils/logger";
import { ContentCard } from "@components/organisms/ContentCard";
import { EventCode, searchMetadataString } from "core/calendar/definitions";
import { AppointmentEvent } from "@components/atoms/AppointmentEvent";
import { MembershipPopup } from "@components/molecules/MembershipPopup";

dayjs.extend(relativeTime)
dayjs.extend(calendar)


type AssistanceType = 'helps' | 'assistance';

export const HomeProfessional = () => {
    const { mutate: mutateCreate } = useCreate();
    const { mutate: mutateUpdate } = useUpdate();
    const { translate } = useTranslation()
    const go = useGo()
    const { data: canUseHelps } = useCan({ action: 'show', resource: 'helps' })
    const { data: canUseAssistance } = useCan({ action: 'show', resource: 'assistance' })


    const getAvailability = (type: AssistanceType) => {
        return useList<IAvailability, HttpError, IAvailability>({
            resource: "availability",
            sorters: [
                {
                    field: 'updated_at',
                    order: 'desc',
                },
            ],
            filters: [
                {
                    field: "type",
                    operator: "eq",
                    value: type,
                },
            ],
            pagination: {
                current: 1,
                pageSize: 1,
            },
            meta: {
                fields: [
                    'id',
                    'status',
                    'type',
                    'updated_at',
                ],
            }
        });
    }

    const { data: dataHelps, isLoading: isLoadingHelps } = getAvailability('helps');
    const { data: dataAssistance, isLoading: isLoadingAssistance } = getAvailability('assistance');

    let lastHelpsStatus = null;
    let lastAssistanceStatus = null;

    if (!isLoadingHelps && !isLoadingAssistance) {
        const lastHelps = dataHelps?.data[0];
        const lastAssistance = dataAssistance?.data[0];
        lastHelpsStatus = lastHelps?.status === 'available' ? true : false;
        lastAssistanceStatus = lastAssistance?.status === 'available' ? true : false;
    }

    const createAvailability = (type: AssistanceType) => {
        mutateCreate({
            resource: "availability",
            values: {
                status: 'available',
                type: type,
            },
        });
    }

    const onChange = (status: State, lasts: IAvailability[] | undefined, type: AssistanceType) => {
        logger.debug(`Lasts ${type}`, lasts, status);
        const lastsIsArray = Array.isArray(lasts);
        const last = lastsIsArray ? lasts[0] : null;
        if (status === State.Available) {
            if (lastsIsArray && !last) {
                logger.debug(`Do not exists ${type}.`);
                createAvailability(type);
            } else if (last?.status === 'not-available') {
                logger.debug(`Already exists ${type}, and is Not Available, so create a new one.`);
                createAvailability(type);
            } else if (last?.status === 'available') {
                logger.debug(`Do not create a new ${type}, the last one is already available.`);
            } else {
                logger.debug(`Do not create a new ${type}, do not meet requirements.`);
            }
        } else {
            if (last?.status === 'available') {
                logger.debug(`Already exists ${type}, and is Available, so update it to not-available.`);
                mutateUpdate({
                    resource: "availability",
                    id: last?.id,
                    values: {
                        status: 'not-available'
                    },
                });
            } else {
                logger.debug(`Do not close existing ${type}, the last one is already not-available.`);
            }
        }
    }

    const onChangeHelps = (status: State) => {
        onChange(status, dataHelps?.data, 'helps');
    }

    const onChangeAssistance = (status: State) => {
        onChange(status, dataAssistance?.data, 'assistance');
    }


    /*
    * Appointments
    */
    const calendarId = useAppCalendar()
    // const calendarId = null

    const date = useMemo(() => dayjs(), [])
    const {
        data: events,
        isLoading: isLoadingAppoiments,
    } = useList<calendar_v3.Schema$Event, HttpError, calendar_v3.Schema$Event>({
        dataProviderName: 'gapi',
        resource: `calendar/calendars/:calendar/events`,
        meta: {
            params: {
                calendar: calendarId?.appointments
            }
        },
        queryOptions: {
            enabled: !!calendarId,
        },
        sorters: [
            {
                field: 'startTime',
                order: 'asc'
            }
        ],
        pagination: {
            pageSize: 1,
        },
        filters: [
            {
                field: 'timeMin',
                operator: 'eq',
                value: date.toISOString()
            },
            {
                field: 'showDeleted',
                operator: 'eq',
                value: false
            },
            {
                field: 'singleEvents',
                operator: 'eq',
                value: true
            },
            {
                field: 'q',
                operator: 'eq',
                value: searchMetadataString({ code: EventCode.APPOINTMENT })
            }
        ]
    })
    const followingEvent = events?.data[0]
    const followingDate = followingEvent ? dayjs(followingEvent.start!.dateTime) : null
    // logger.debug(followingEvent)

    /*
    * Content
    */
    const blogId = useAtomValue(blogIdAtom)

    const {
        data,
        // isError,
        // isLoading,
        // hasNextPage,
        // fetchNextPage,
        // isFetchingNextPage,
    } = useList<blogger_v3.Schema$Post, HttpError, blogger_v3.Schema$Post>({
        dataProviderName: 'gapi',
        resource: `blogger/blogs/:blogId/posts`, //https://developers.google.com/calendar/api/v3/reference/events/list
        meta: {
            params: {
                blogId: blogId
            },
            fields: ['title', 'images', 'id']
        },
        queryOptions: {
            enabled: !!blogId,
        },
        sorters: [
            {
                field: 'PUBLISHED',
                order: 'desc'
            }
        ],
        pagination: {
            pageSize: 5,
        },
        filters: [
            {
                field: 'status',
                operator: 'eq',
                value: 'LIVE'
            },
            {
                field: 'fetchImages',
                operator: 'eq',
                value: true
            },
            {
                field: 'labels',
                operator: 'eq',
                value: ['pro']
            }
        ],
    })

    const posts = data?.data

    // logger.debug("can use Helps", canUseHelps?.can)

    return <CanAccess resource="home" action="show" params={{ validateSubscription: true }}
        fallback={<MembershipPopup />}>
        <div className="flex flex-col px-4 pb-4 prose prose-figure:m-0 max-w-4xl prose-headings:text-inherit">
            <div className="flex-row">
                <h2 className="mt-0">{translate("page.home.topButtons.activations")}</h2>
                <div className="flex flex-wrap gap-2">
                    <CircularButtonWithToggle
                        icon={FaLifeRing}
                        className={isLoadingHelps ? 'skeleton' : ''}
                        text={translate("page.home.topButtons.help.title")}
                        data-tip={!canUseHelps?.can ? translate("page.home.topButtons.helps.no-helps") : ''}
                        delayedOnChangeState={onChangeHelps}
                        lastStatus={lastHelpsStatus}
                        disabled={!canUseHelps?.can}
                    />
                    {/* fallback={<MembershipPopup />}> */}
                    <CircularButtonWithToggle
                        icon={MdAssistWalker}
                        className={isLoadingAssistance ? 'skeleton' : ''}
                        text={translate("page.home.topButtons.assistance.title")}
                        data-tip={!canUseAssistance?.can ? translate("page.home.topButtons.assistance.no-assistance") : ''}
                        delayedOnChangeState={onChangeAssistance}
                        lastStatus={lastAssistanceStatus}
                        disabled={!canUseAssistance?.can}
                    />
                    <Link go={{
                        to: {
                            resource: "calendar",
                            action: "list"
                        },
                        query: {
                            'create-event': true
                        }
                    }}>
                        <button
                            data-tip={translate("page.home.topButtons.appointment.tip")}
                            className="btn btn-primary h-12">
                            <FaCalendarPlus />{translate("page.home.topButtons.appointment.title")}
                        </button>
                    </Link>
                </div>
            </div>
            <h2>{translate("page.home.appointments")}</h2>
            <div className="dropdown">
                <div tabIndex={0} role="button" className="">
                    <InfoCard
                        className={isLoadingAppoiments ? 'skeleton' : ''}
                        title={translate("page.home.next-appointment")}
                        description={!isLoadingAppoiments ? (followingDate ? <>
                            <span className="capitalize">{`${followingDate.format('dddd, HH:mm')}`}</span>
                            <span className="badge badge-primary ml-2">{`${date.to(followingDate)}`}</span>
                        </> : translate('page.home.no-following-appointment')) : '-'}
                        icon={FaCalendarDays}
                    />
                </div>
                {followingEvent && <AppointmentEvent
                    tabIndex={0}
                    className="dropdown-content z-10 mt-1 not-prose"
                    event={followingEvent}
                />}
            </div>
            {/* <div className="flex-row" >
            <h2>{translate("page.home.my-stadistics")}</h2>
            <div className="grid grid-cols-2 gap-4">
                <InfoCard
                    title="Citas agendadas para esta semana"
                    description=""
                />
                <InfoCard
                    title="Mis requirimiento"
                    description=""
                />
                <InfoCard
                    title="Cuidadores"
                    description=""
                />
                <InfoCard
                    title="Alertas atendidas"
                    description=""
                />
            </div>
        </div> */}
            <div className="flex-row" >
                <h2>{`${translate("page.home.news")} / ${translate("page.home.knowledge")}`}</h2>
                <div className="carousel carousel-center w-full">
                    {posts?.map(post => {
                        const image = post.images?.[0].url

                        return <div key={post.id!} className="carousel-item w-[90%] md:w-[45%] lg:w-[30%] xl:w-[25%] p-2">
                            <ContentCard post={post} />
                        </div>
                    })}
                </div>
            </div>
        </div>
    </CanAccess>
}
